import { useState, useEffect, useRef, useMemo } from 'react';
import EventEmitter from 'react-native-eventemitter';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import useUnsavedElementsAlerter from '../../../hooks/useUnsavedElementsAlerter';
import { getPermissionsByCommand } from '../../../utils/partialPermissionUtils';
import { viewService } from '../../../services/views.service';
import { trackingEvent } from '../../../analytics';
import { getBasicAmplitudEventProperties } from '../../../analytics/utils';

export const useGoToLookaheadButton = () => {
  const { t } = useTranslation();

  const { companyId, projectSelected, sectorSelected } = useSelector(
    (state) => ({
      companyId: state.companyState.currentCompany.id,
      projectSelected: state.projectState.projectSelected,
      sectorSelected: state.projectState.sectorSelected
    })
  );

  const isInitialMount = useRef(true);
  const previousSectorSelectedRef = useRef(sectorSelected);

  const shouldFetchHasView = () => {
    if (
      isInitialMount.current ||
      sectorSelected !== previousSectorSelectedRef.current
    ) {
      isInitialMount.current = false;
      previousSectorSelectedRef.current = sectorSelected;

      return true;
    }

    return false;
  };

  const [isShineButton, setIsShineButton] = useState(false);
  const [isHasView, setIsHasView] = useState(false);

  const { data: hasViewData, isLoading } = useQuery({
    queryKey: ['hasView', projectSelected, sectorSelected],
    queryFn: () =>
      viewService.showByProject({
        scheduleId: sectorSelected,
        projectId: projectSelected
      }),
    refetchOnWindowFocus: false,
    cacheTime: 0,
    retry: false,
    enabled: shouldFetchHasView()
  });

  const { jsx, callback } = useUnsavedElementsAlerter(t);

  const hasPermission = useMemo(
    () => getPermissionsByCommand('BUTTON_GO_TO_LOOKAHEAD'),
    []
  );

  const routeLookahead = useMemo(
    () =>
      `/${companyId}/project/${projectSelected}/schedule/${sectorSelected}/lookahead`,
    [companyId, projectSelected, sectorSelected]
  );

  useEffect(() => {
    if (hasViewData) {
      setIsHasView(hasViewData.length > 0);
    }
  }, [hasViewData]);

  useEffect(() => {
    if (window.isShineButtonGoToLookahead) {
      setIsShineButton(true);
      window.isShineButtonGoToLookahead = false;
    }
  }, [window.isShineButtonGoToLookahead]);

  const handleAnimationEnd = () => {
    setIsShineButton(false);
    window.isShineButtonGoToLookahead = false;
  };

  const handleClick = () => {
    if (!hasPermission) {
      return;
    }

    trackingEvent('plan_4_weeks_button_clicked', {
      ...getBasicAmplitudEventProperties(),
      button_label: t(
        isHasView
          ? 'master_plan.button_go_to_lookahead_with_view'
          : 'master_plan.button_go_to_lookahead_without_view'
      ),
      has_commit: isHasView
    });

    EventEmitter.emit('changeMainRoute', {
      route: (history) => {
        history.push(routeLookahead);
      }
    });
  };

  const unsavedChangesListener = () => {
    if (callback()) {
      return callback(handleClick);
    }

    handleClick();
  };

  return {
    hasPermission,
    unsavedChangesListener,
    handleAnimationEnd,
    isShineButton,
    isHasView,
    jsx,
    isLoading
  };
};
